import * as React from 'react';

import {
  ComparisonSpecType,
  isPercentageFormat,
  isStaticComparison,
  isValidConstantComparison,
  makeStaticComparisonSpec,
  StaticComparisonSpec,
} from '@tableau/metrics-engine';
import { TextFieldWidget } from '@tableau/widgets';

import { Messages } from '../../../../gen-src/Localize';
import { comparisonInput } from '../configurationStyles';
import { shakeInputIfInvalid } from '../../../shakeIfInvalid/shakeInputIfInvalid';
import { formatAuthoredPercentOutOf100 } from '../../../formatting/formatting';

export interface ConstantComparisonValueInputProps {
  validationCounter: number;

  formatString?: string;

  selectedComparison: ComparisonSpecType | null;
  onSelectedComparisonChange: (comparison: StaticComparisonSpec | null) => void;

  disabled?: boolean;
}

export const ConstantComparisonValueInput = (props: ConstantComparisonValueInputProps): JSX.Element | null => {
  if (props.selectedComparison !== null && !isStaticComparison(props.selectedComparison)) {
    return null;
  }

  const initialSelectedValueString = getInitialSelectedValueString(props.selectedComparison, props.formatString);

  const [inputValue, setInputValue] = React.useState<string>(initialSelectedValueString);
  const clearValue = () => {
    setInputValue('');
    props.onSelectedComparisonChange(null);
  };

  const [hasBeenEdited, setHasBeenEdited] = React.useState(false);

  const updateStateValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasBeenEdited(true);

    const input = e.target.value;
    let value = parseInput(input, props.formatString);

    if (isValidConstantComparison(value) || input === '') {
      setInputValue(input);

      if (value === null) {
        props.onSelectedComparisonChange(null);
      } else {
        props.onSelectedComparisonChange(makeStaticComparisonSpec('', value));
      }
    }
  };

  const inputRef = React.createRef<HTMLInputElement>();
  // We only want the input to focus when it is initially rendered and when
  // the selected comparison is null. The empty array as the second useEffect
  // argument accomplishes the "only on initial render".
  React.useEffect(() => {
    if (props.selectedComparison === null) {
      inputRef?.current?.focus();
    }
  }, []);

  const onBlur = () => {
    setHasBeenEdited(true);
  };

  const invalid = hasBeenEdited && !isValidConstantComparison(parseInput(inputValue, props.formatString));

  // On validation error, the widget shakes to grab the user's attention if it
  // doesn't have a valid value.  For this particular input, we only want to do
  // this if the value has been edited, since we don't want to an error state on
  // initial render.
  React.useEffect(shakeInputIfInvalid(invalid, inputRef), [props.validationCounter]);

  return (
    <TextFieldWidget
      type="number"
      max={Number.MAX_SAFE_INTEGER}
      min={Number.MIN_SAFE_INTEGER}
      className={comparisonInput}
      label={Messages.ConstantComparisonValue()}
      testId="metric-constant-comparison"
      onChange={updateStateValue}
      onClear={clearValue}
      onBlur={onBlur}
      value={inputValue}
      ref={inputRef}
      valid={invalid ? false : undefined}
      message={invalid ? Messages.ConstantComparisonInputErrorMessage() : <br />}
      disabled={props.disabled}
    />
  );
};

function getInitialSelectedValueString(comparison: StaticComparisonSpec | null, formatString?: string): string {
  if (comparison?.value === undefined) {
    return '';
  }

  if (isPercentageFormat(formatString)) {
    return formatAuthoredPercentOutOf100(comparison.value);
  }

  return comparison.value.toString();
}

function parseInput(input: string, formatString?: string): number | null {
  const parsedInput = parseFloat(input);
  let value = isNaN(parsedInput) ? null : parsedInput;

  if (value !== null && isPercentageFormat(formatString)) {
    value /= 100;
  }
  return value;
}
